import React, { useState, useMemo } from 'react'
import { Plus, Edit2, Trash2, Search, TrendingUp } from 'lucide-react'
import { useCRMStore } from '../store/crmStore'

export default function LeadsPage() {
  const leads = useCRMStore((state) => state.leads)
  const addLead = useCRMStore((state) => state.addLead)
  const updateLead = useCRMStore((state) => state.updateLead)
  const deleteLead = useCRMStore((state) => state.deleteLead)
  const convertLeadToCustomer = useCRMStore((state) => state.convertLeadToCustomer)

  const [searchTerm, setSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [selectedLead, setSelectedLead] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    source: 'Website',
    status: 'New',
    score: 50,
  })

  const filteredLeads = useMemo(() => {
    return leads.filter(lead =>
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.company.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [leads, searchTerm])

  const handleOpenModal = (lead = null) => {
    if (lead) {
      setSelectedLead(lead)
      setFormData(lead)
    } else {
      setSelectedLead(null)
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        source: 'Website',
        status: 'New',
        score: 50,
      })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedLead(null)
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      source: 'Website',
      status: 'New',
      score: 50,
    })
  }

  const handleSave = () => {
    if (!formData.name || !formData.email || !formData.company) {
      alert('Please fill in all required fields')
      return
    }

    if (selectedLead) {
      updateLead(selectedLead.id, formData)
    } else {
      addLead(formData)
    }
    handleCloseModal()
  }

  const handleDelete = (leadId) => {
    if (confirm('Are you sure you want to delete this lead?')) {
      deleteLead(leadId)
    }
  }

  const handleConvertLead = (lead) => {
    if (confirm(`Convert "${lead.name}" to a customer?`)) {
      convertLeadToCustomer(lead.id, lead)
      alert('Lead converted to customer successfully!')
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-500/20 text-blue-300'
      case 'Contacted': return 'bg-purple-500/20 text-purple-300'
      case 'Qualified': return 'bg-green-500/20 text-green-300'
      case 'Disqualified': return 'bg-red-500/20 text-red-300'
      default: return 'bg-slate-500/20 text-slate-300'
    }
  }

  const getScoreBadge = (score) => {
    if (score >= 80) return 'bg-green-500/20 text-green-300'
    if (score >= 60) return 'bg-yellow-500/20 text-yellow-300'
    return 'bg-red-500/20 text-red-300'
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Leads Management</h1>
              <p className="text-slate-400">Manage and qualify sales leads</p>
            </div>
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold py-3 px-6 rounded-lg transition-all"
            >
              <Plus size={20} />
              Add Lead
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-slate-400 text-sm mb-2">Total Leads</p>
              <p className="text-3xl font-bold text-white">{leads.length}</p>
            </div>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-slate-400 text-sm mb-2">New</p>
              <p className="text-3xl font-bold text-blue-400">{leads.filter(l => l.status === 'New').length}</p>
            </div>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-slate-400 text-sm mb-2">Qualified</p>
              <p className="text-3xl font-bold text-green-400">{leads.filter(l => l.status === 'Qualified').length}</p>
            </div>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-slate-400 text-sm mb-2">Avg Score</p>
              <p className="text-3xl font-bold text-yellow-400">
                {leads.length > 0 ? Math.round(leads.reduce((sum, l) => sum + l.score, 0) / leads.length) : 0}
              </p>
            </div>
          </div>

          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-3 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-blue-500/20 rounded-lg text-white focus:outline-none focus:border-blue-400/50"
              />
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-blue-500/20 bg-slate-900/50">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Company</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Score</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((lead) => (
                    <tr key={lead.id} className="border-b border-blue-500/10 hover:bg-slate-800/50">
                      <td className="px-6 py-4 text-white font-medium">{lead.name}</td>
                      <td className="px-6 py-4 text-slate-400">{lead.company}</td>
                      <td className="px-6 py-4">
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold ${getStatusColor(lead.status)}`}>
                          {lead.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold ${getScoreBadge(lead.score)}`}>
                          {lead.score}
                        </span>
                      </td>
                      <td className="px-6 py-4 flex gap-2">
                        <button
                          onClick={() => handleOpenModal(lead)}
                          className="p-2 hover:bg-slate-700 rounded text-blue-400"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(lead.id)}
                          className="p-2 hover:bg-slate-700 rounded text-red-400"
                        >
                          <Trash2 size={18} />
                        </button>
                        {lead.status === 'Qualified' && (
                          <button
                            onClick={() => handleConvertLead(lead)}
                            className="px-3 py-1 text-xs bg-green-500/20 text-green-300 rounded font-semibold"
                          >
                            Convert
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {filteredLeads.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-400 text-lg">No leads found</p>
            </div>
          )}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 max-w-2xl w-full mx-4">
              <div className="flex justify-between items-center p-6 border-b border-blue-500/20">
                <h2 className="text-2xl font-bold text-white">
                  {selectedLead ? 'Edit Lead' : 'Add New Lead'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-slate-400 hover:text-white"
                >
                  ✕
                </button>
              </div>

              <div className="p-6 space-y-4">
                <input
                  type="text"
                  placeholder="Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white"
                />
                <input
                  type="email"
                  placeholder="Email *"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white"
                />
                <input
                  type="text"
                  placeholder="Company *"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white"
                />
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white"
                >
                  <option>New</option>
                  <option>Contacted</option>
                  <option>Qualified</option>
                </select>
              </div>

              <div className="flex justify-end gap-4 p-6 border-t border-blue-500/20">
                <button
                  onClick={handleCloseModal}
                  className="px-6 py-2 rounded border border-slate-600 text-slate-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  )
}
